import {
  getLicenseTypeConfigurationOptions,
  getLicenseTypeQuestionsOptions,
  lookupParcelByAddressOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { lookupParcelByApn } from "@/hera-api/client/sdk.gen";
import type { JurisdictionResponseSchema } from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import type { AddressDetails } from "@/types/address";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "../../ui/alert";
import { Button } from "../../ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import ErrorComponent from "../../ui/error";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import LoadingScreen from "../../ui/loading-screen";
import { EarlyInformationalQuestions } from "./early-questions";
import { FeeInformation } from "./fee-information";

interface AddressDetailsFormProps {
  addressDetails?: AddressDetails;
  /** When set (e.g. marketing deep link), address lookup must resolve to this jurisdiction. */
  expectedJurisdictionId?: string;
  onPrevious: () => Promise<void>;
  onNext: (data: {
    jurisdiction: JurisdictionResponseSchema;
    apn: string | null;
    geocoded_address1: string;
    geocoded_address2: string | null;
    geocoded_city: string;
    geocoded_state: string;
    geocoded_zipcode: string;
    geocoded_lat: number;
    geocoded_lng: number;
  }) => Promise<void>;
  stepNumber: number;
  licenseTypeId?: string;
  /** When true (e.g. add unregistered property flow), hide fee information, legal disclaimer, and additional questions. */
  hideFeesAndLegalDisclaimer?: boolean;
}

export function AddressLookupVerificationForm({
  addressDetails,
  expectedJurisdictionId,
  onPrevious,
  onNext,
  stepNumber,
  licenseTypeId,
  hideFeesAndLegalDisclaimer = false,
}: AddressDetailsFormProps) {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const [userApn, setUserApn] = useState<string>("");
  const [manualApnDetails, setManualApnDetails] = useState<any>(null);
  const [isLookingUpApn, setIsLookingUpApn] = useState(false);

  const {
    data,
    isLoading: isQueryLoading,
    error: queryError,
  } = useQuery({
    ...lookupParcelByAddressOptions({
      body: {
        address1: addressDetails?.address1 ?? "",
        address2: addressDetails?.address2 ?? null,
        city: addressDetails?.city ?? "",
        state: addressDetails?.state ?? "",
        zipcode: addressDetails?.zipcode ?? "",
        license_type_id: licenseTypeId || null,
        apn: addressDetails?.apn?.trim() ? addressDetails.apn.trim() : null,
      },
    }),
    enabled: isAuthSetupComplete,
  });

  // Set default APN when data loads
  useEffect(() => {
    if (data?.apn_options && data.apn_options.length > 0 && !userApn) {
      setUserApn(data.apn_options[0].apn);
    }
  }, [data?.apn_options, userApn]);

  // Fetch configuration data when we have both license type and jurisdiction
  const { data: configurationData } = useQuery({
    ...getLicenseTypeConfigurationOptions({
      path: {
        license_type_id: licenseTypeId || "",
        jurisdiction_id: data?.matched_jurisdiction?.id || "",
      },
    }),
    enabled:
      isAuthSetupComplete &&
      !!licenseTypeId &&
      !!data?.matched_jurisdiction?.id,
  });

  // Fetch questions data when we have both license type and jurisdiction
  const { data: localQuestionsData } = useQuery({
    ...getLicenseTypeQuestionsOptions({
      path: {
        license_type_id: licenseTypeId || "",
        jurisdiction_id: data?.matched_jurisdiction?.id || "",
      },
    }),
    enabled:
      isAuthSetupComplete &&
      !!licenseTypeId &&
      !!data?.matched_jurisdiction?.id,
  });

  // APN lookup mutation
  const apnLookupMutation = useMutation({
    mutationFn: (body: {
      apn: string;
      jurisdiction_code: string | null;
      latitude: number;
      longitude: number;
    }) => lookupParcelByApn({ body }),
    onSuccess: (response) => {
      if (response.data?.apn_option) {
        setManualApnDetails(response.data.apn_option);
        setLookupFailed(false);
        // Replace the user's typed APN with the formatted version from ATTOM
        setUserApn(response.data.apn_option.apn);
      } else {
        setManualApnDetails(null);
        setLookupFailed(true);
      }
      setIsLookingUpApn(false);
    },
    onError: (error) => {
      console.error("APN lookup failed:", error);
      setManualApnDetails(null);
      setIsLookingUpApn(false);
      setLookupFailed(true);
      // Note: We don't clear userApn here - the APN is still valid even if lookup fails
    },
  });

  // State to track lookup failure
  const [lookupFailed, setLookupFailed] = useState(false);

  const handleApnLookup = () => {
    if (!userApn.trim()) return;

    setIsLookingUpApn(true);
    setManualApnDetails(null);
    setLookupFailed(false);

    apnLookupMutation.mutate({
      apn: userApn.trim(),
      jurisdiction_code: data?.matched_jurisdiction?.code || null,
      latitude: data?.geocoded_lat || 0,
      longitude: data?.geocoded_lng || 0,
    });
  };

  const handlePrevious = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onPrevious();
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!data) {
      console.error("BUG: No address data found", data);
      return;
    }

    if (!data.matched_jurisdiction) {
      console.error("BUG: No matched jurisdiction found", data);
      return;
    }

    if (
      expectedJurisdictionId &&
      data.matched_jurisdiction.id !== expectedJurisdictionId
    ) {
      return;
    }

    if (
      !data.geocoded_lat ||
      !data.geocoded_lng ||
      !data.geocoded_address1 ||
      !data.geocoded_city ||
      !data.geocoded_state ||
      !data.geocoded_zipcode
    ) {
      console.error("BUG: Missing geocoded address data", data);
      return;
    }

    onNext({
      jurisdiction: data.matched_jurisdiction,
      apn: userApn.trim() || null,
      geocoded_address1: data.geocoded_address1,
      geocoded_address2:
        data.geocoded_address2 || (addressDetails?.address2 ?? null), // Geocoding probably won't return anything for this
      geocoded_city: data.geocoded_city,
      geocoded_state: data.geocoded_state,
      geocoded_zipcode: data.geocoded_zipcode,
      geocoded_lat: data.geocoded_lat,
      geocoded_lng: data.geocoded_lng,
    });
  };

  let errorAndLoadingContent = null;
  if (!isAuthSetupComplete) {
    errorAndLoadingContent = (
      <div className="flex justify-center items-center py-8">
        <LoadingScreen message="Authenticating..." />
      </div>
    );
  } else if (isQueryLoading) {
    errorAndLoadingContent = (
      <div className="flex justify-center items-center py-8">
        <LoadingScreen delay={0} message="Looking up address..." />
      </div>
    );
  } else if (queryError) {
    errorAndLoadingContent = (
      <ErrorComponent
        title={getQueryErrorHeading(queryError)}
        message={getQueryErrorMessage(queryError)}
      />
    );
  } else if (!data?.matched_jurisdiction) {
    if (!data?.tiger_name) {
      // Format the address for display
      const formattedAddress = [
        addressDetails?.address1,
        addressDetails?.address2,
        addressDetails?.city,
        addressDetails?.state,
        addressDetails?.zipcode,
      ]
        .filter(Boolean)
        .join(", ");

      errorAndLoadingContent = (
        <ErrorComponent title="Could not determine jurisdiction">
          <p>
            {formattedAddress
              ? `"${formattedAddress}" couldn't be identified.`
              : "The property address couldn't be identified."}{" "}
            Please contact Hera Property Registry support.
          </p>
        </ErrorComponent>
      );
    } else {
      errorAndLoadingContent = (
        <ErrorComponent title="Unsupported jurisdiction">
          <p>
            Your address was determined to be in &apos;{data?.tiger_name}&apos;,
            but this jurisdiction is not supported by Hera Property Registry
            {licenseTypeId ? " for the selected license type" : ""}.
          </p>
          <p>
            If you believe this is an error, please contact Hera Property
            Registry support.
          </p>
        </ErrorComponent>
      );
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Step {stepNumber}: Confirm Address and Jurisdiction
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {errorAndLoadingContent ? (
          errorAndLoadingContent
        ) : (
          <>
            <div className="space-y-2">
              <Label>Address</Label>
              <div className="p-2 border text-sm rounded-md bg-muted/50">
                {data?.geocoded_formatted_address ?? "Unknown"}
              </div>
              <p className="text-xs text-muted-foreground">
                This is the address that we used to determine the jurisdiction.
                It might differ slightly from what you typed, so double-check
                that it is correct.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Detected Jurisdiction</Label>
              <div className="p-2 border text-sm rounded-md bg-muted/50">
                {data?.matched_jurisdiction?.name ?? "Unknown"}
              </div>
              <p className="text-xs text-muted-foreground">
                This is the jurisdiction that will process your registration.
              </p>
            </div>
            {expectedJurisdictionId &&
              data?.matched_jurisdiction &&
              data.matched_jurisdiction.id !== expectedJurisdictionId && (
                <Alert variant="destructive">
                  <AlertDescription>
                    The address resolves to{" "}
                    <span className="font-medium">
                      {data.matched_jurisdiction.name}
                    </span>
                    , which is not the community you chose from the directory.
                    Use an address in that community, or go back and pick a
                    different community.
                  </AlertDescription>
                </Alert>
              )}
            <div className="space-y-2">
              <Label>Property APN (Assessor&apos;s Parcel Number)</Label>
              <div className="space-y-2">
                {data?.apn_options && data.apn_options.length > 0 ? (
                  <div className="space-y-3">
                    {data.apn_options.map((option) => (
                      <button
                        key={option.apn}
                        type="button"
                        className={`w-full text-left p-3 border rounded-md cursor-pointer transition-colors ${
                          userApn === option.apn
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                        }`}
                        onClick={() => setUserApn(option.apn)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <input
                                type="radio"
                                name="apn-option"
                                checked={userApn === option.apn}
                                onChange={() => setUserApn(option.apn)}
                                className="mt-1"
                              />
                              <span className="font-medium">
                                APN: {option.apn}
                              </span>
                            </div>
                            <div className="text-sm text-muted-foreground ml-6">
                              <div>{option.address}</div>
                              {option.legal_description && (
                                <div className="mt-1">
                                  <span className="font-medium">
                                    Legal Description:
                                  </span>{" "}
                                  {option.legal_description}
                                </div>
                              )}
                              {option.property_type && (
                                <div>
                                  <span className="font-medium">
                                    Property Type:
                                  </span>{" "}
                                  {option.property_type}
                                </div>
                              )}
                              {option.lot_size && (
                                <div>
                                  <span className="font-medium">Lot Size:</span>{" "}
                                  {option.lot_size}
                                </div>
                              )}
                              {option.year_built && (
                                <div>
                                  <span className="font-medium">
                                    Year Built:
                                  </span>{" "}
                                  {option.year_built}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}

                    {/* Manual entry option */}
                    <div
                      className={`w-full text-left p-3 border rounded-md transition-colors ${
                        userApn &&
                        !data.apn_options?.some((opt) => opt.apn === userApn)
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="apn-option"
                          checked={
                            !!(
                              userApn &&
                              !data.apn_options?.some(
                                (opt) => opt.apn === userApn,
                              )
                            )
                          }
                          onChange={() => {
                            // This will be handled by the input's onChange
                          }}
                          className="mt-1"
                        />
                        <span className="font-medium">APN</span>
                        <Input
                          id="manual-apn-input"
                          placeholder="Type your APN here"
                          value={
                            userApn &&
                            !data.apn_options?.some(
                              (opt) => opt.apn === userApn,
                            )
                              ? userApn
                              : ""
                          }
                          onChange={(e) => {
                            setUserApn(e.target.value);
                          }}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={handleApnLookup}
                          disabled={
                            !userApn.trim() ||
                            isLookingUpApn ||
                            !data?.geocoded_lat ||
                            !data?.geocoded_lng
                          }
                        >
                          {isLookingUpApn ? (
                            "Looking up..."
                          ) : !data?.geocoded_lat || !data?.geocoded_lng ? (
                            "No coordinates"
                          ) : (
                            <>
                              <Search className="h-4 w-4 mr-1" />
                              Lookup
                            </>
                          )}
                        </Button>
                      </div>

                      {/* Display lookup results */}
                      {manualApnDetails && (
                        <div className="text-sm text-muted-foreground ml-6 mt-3">
                          <div>{manualApnDetails.address}</div>
                          {manualApnDetails.legal_description && (
                            <div className="mt-1">
                              <span className="font-medium">
                                Legal Description:
                              </span>{" "}
                              {manualApnDetails.legal_description}
                            </div>
                          )}
                          {manualApnDetails.property_type && (
                            <div>
                              <span className="font-medium">
                                Property Type:
                              </span>{" "}
                              {manualApnDetails.property_type}
                            </div>
                          )}
                          {manualApnDetails.lot_size && (
                            <div>
                              <span className="font-medium">Lot Size:</span>{" "}
                              {manualApnDetails.lot_size}
                            </div>
                          )}
                          {manualApnDetails.year_built && (
                            <div>
                              <span className="font-medium">Year Built:</span>{" "}
                              {manualApnDetails.year_built}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Display lookup failure message */}
                      {lookupFailed && userApn.trim() && (
                        <div className="text-sm text-muted-foreground ml-6 mt-3">
                          <div className="text-amber-600">
                            APN not found in county data. If you are sure it's
                            correct please continue with this APN.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <Input
                    placeholder="Enter APN if known"
                    value={userApn || ""}
                    onChange={(e) => {
                      setUserApn(e.target.value);
                    }}
                  />
                )}
                <p className="text-xs text-muted-foreground">
                  This is the unique identifier for your property. Select one of
                  the recommended options above, or enter your own APN.
                </p>
              </div>
            </div>

            {localQuestionsData && !hideFeesAndLegalDisclaimer && (
              <EarlyInformationalQuestions questionsData={localQuestionsData} />
            )}

            {!hideFeesAndLegalDisclaimer && (
              <>
                {configurationData && (
                  <FeeInformation configurationData={configurationData} />
                )}

                {!configurationData && data?.matched_jurisdiction && (
                  <div className="space-y-2 p-4 border rounded-md bg-muted/50">
                    <h3 className="font-medium text-sm">Fee Information</h3>
                    <p className="text-xs text-muted-foreground">
                      Fee information will be available once you proceed to the
                      next step where we can determine the specific requirements
                      for {data.matched_jurisdiction.name}.
                    </p>
                  </div>
                )}

                <Alert variant="warning">
                  <AlertDescription>
                    <p className="font-medium">
                      LEGAL NOTICE: By proceeding, you acknowledge that it is
                      your legal responsibility to ensure that the jurisdiction
                      and APN information (if present) provided is accurate and
                      complete, and that you have reviewed the ordinance
                      requirements.
                    </p>
                    <p className="font-medium">
                      Providing incorrect information may constitute a violation
                      of local ordinances and may result in penalties, fines, or
                      legal action. The property owner assumes all liability for
                      any inaccuracies in the registration information. If you
                      are uncertain about any details, please contact Hera
                      Registry for support before proceeding.
                    </p>
                  </AlertDescription>
                </Alert>
              </>
            )}
          </>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={handlePrevious}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>
        <Button
          className="disabled:opacity-20 disabled:cursor-not-allowed"
          onClick={handleNext}
          disabled={
            !data?.matched_jurisdiction ||
            !!(
              expectedJurisdictionId &&
              data.matched_jurisdiction.id !== expectedJurisdictionId
            )
          }
        >
          Next
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
}
